import React, { useState, useEffect } from 'react';
import LayoutHeader from '../../../../layout/LayoutHeader';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { setSuccessAlert } from '../../../../store/alert/alertSlice';
import NotificationSettings from '../../../../components/NotificationSettings';

// Settings key for localStorage
const REMINDER_SETTINGS_KEY = 'dizee_reminder_settings';

// Get user's reminder settings
const getReminderSettings = () => {
    try {
        const settings = localStorage.getItem(REMINDER_SETTINGS_KEY);
        return settings ? JSON.parse(settings) : {};
    } catch (error) {
        console.error('Error reading reminder settings:', error);
        return {};
    }
};

// Save user's reminder settings
const saveReminderSettings = (newSettings) => {
    try {
        const currentSettings = getReminderSettings();
        const updatedSettings = { ...currentSettings, ...newSettings };
        localStorage.setItem(REMINDER_SETTINGS_KEY, JSON.stringify(updatedSettings));
        return updatedSettings;
    } catch (error) {
        console.error('Error saving reminder settings:', error);
    }
};

export default function Notifications() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [settings, setSettings] = useState({});
    const [showPushSettings, setShowPushSettings] = useState(false);

    // Load settings on component mount
    useEffect(() => {
        const currentSettings = getReminderSettings();
        setSettings(currentSettings);
    }, []);

    // Toggle release workflow modals
    const toggleReleaseWorkflowModals = () => {
        const newValue = !settings.disableReleaseWorkflowModals;
        const newSettings = saveReminderSettings({ 
            disableReleaseWorkflowModals: newValue 
        });
        setSettings(newSettings);
        
        const message = newValue 
            ? 'Release workflow modals have been disabled'
            : 'Release workflow modals have been enabled';
        dispatch(setSuccessAlert(message));
    };

    return (
        <LayoutHeader>
            <div className="w-[390px] flex flex-col justify-start items-center relative z-[0]">
                <div className="px-4 my-[50px] flex w-full justify-between">
                    <p className="text-white">Notifications</p>
                    <div className='flex gap-[20px]'>
                        <p onClick={() => navigate('/more/setting/advanced')} className='text-white cursor-pointer'>Go back</p>
                    </div>
                </div>

                <div className="w-full px-4 space-y-4">
                    {/* Release Workflow Modals Setting */}
                    <div className="w-full py-[14px] border-b border-[#ffffff1a]">
                        <div className="flex justify-between items-center">
                            <div className="flex-1">
                                <p className="text-white text-[12px] leading-[18px] font-medium">
                                    Release Workflow Popups
                                </p>
                                <p className="text-[rgba(255,255,255,0.6)] text-[10px] leading-[15px] mt-1">
                                    Show congratulations popup when creating new release workflows
                                </p>
                            </div>
                            <button
                                onClick={toggleReleaseWorkflowModals}
                                className={`relative w-8 h-4 rounded-full transition-colors ${
                                    settings.disableReleaseWorkflowModals 
                                        ? 'bg-[rgba(255,255,255,0.2)]' 
                                        : 'bg-[#17A2B8]'
                                }`}
                            >
                                <div
                                    className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                                        settings.disableReleaseWorkflowModals 
                                            ? '' 
                                            : 'translate-x-4'
                                    }`}
                                />
                            </button>
                        </div>
                    </div>

                    {/* Push Notifications */}
                    <div 
                        onClick={() => setShowPushSettings(true)}
                        className="w-full py-[14px] border-b border-[#ffffff1a] cursor-pointer hover:bg-[rgba(255,255,255,0.05)] transition-colors rounded-lg px-2 -mx-2"
                    >
                        <div className="flex justify-between items-center">
                            <div className="flex-1">
                                <p className="text-white text-[12px] leading-[18px] font-medium">
                                    🔔 Push Notifications
                                </p>
                                <p className="text-[rgba(255,255,255,0.6)] text-[10px] leading-[15px] mt-1">
                                    Get push notifications for reminders, bookings, and important events
                                </p>
                            </div>
                            <div className="text-[rgba(255,255,255,0.4)] text-[16px]">›</div>
                        </div>
                    </div>

                    <div className="w-full py-[14px] border-b border-[#ffffff1a] opacity-50">
                        <div className="flex justify-between items-center">
                            <div className="flex-1">
                                <p className="text-white text-[12px] leading-[18px] font-medium">
                                    Email Notifications
                                </p>
                                <p className="text-[rgba(255,255,255,0.6)] text-[10px] leading-[15px] mt-1">
                                    Receive reminder notifications via email (Coming soon)
                                </p>
                            </div>
                            <button
                                disabled
                                className="relative w-8 h-4 rounded-full bg-[rgba(255,255,255,0.2)] transition-colors opacity-50 cursor-not-allowed"
                            >
                                <div className="absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Help text */}
                <div className="w-full px-4 mt-6">
                    <div className="text-[10px] text-[rgba(255,255,255,0.5)] leading-[1.5]">
                        <p>Notification settings control how and when you receive alerts about reminders and workflows.</p>
                        <p className="mt-2">Changes are saved automatically.</p>
                    </div>
                </div>
            </div>

            {/* Push Notification Settings Modal */}
            {showPushSettings && (
                <NotificationSettings
                    onClose={() => setShowPushSettings(false)}
                    showAsModal={true}
                />
            )}
        </LayoutHeader>
    );
} 